import React, { Component } from "react";
import Tab from "react-bootstrap/Tab";
import Nav from "react-bootstrap/Nav";
import { toast } from "react-toastify";
import { PiCheckCircleFill, PiSparkleBold } from "react-icons/pi";
import axios from "../../axios";
import apiError from "../../error";
import Loader from "../loader/loader";
import CampaignName from "./campaign-steps/campaign-name/campaign-name";
import campaignTabs from "../../campaignTabs";
import { getCookie, isTruthyValue } from "../../function";
import { getTemplateIds, normalizeAttackType, normalizeSimulationType, normalizeInteractionType, coerceBoolean } from "./campaign-template-utils";

// Reuse the exact same wizard step components the campaign-create flow uses, so
// this editor renders identically to the campaign template wizard — the only
// differences are: tabs are horizontal at the top (not a left sidebar), the
// "Template Name" step locks the simulation type / attack vector (already chosen
// in the AI chat) while still letting the user review/edit name, category,
// language and tags, and the Email / Landing / Awareness steps carry an
// "Edit with prompt" bar.
import Game from "./campaign-steps/game/game";
import Pages from "./campaign-steps/pages/pages";
import AwarenessPages from "./campaign-steps/pages/awareness-page";
import Training from "./campaign-steps/training/training";
import Templates from "./campaign-steps/templates/templates";
import TemplatesSMS from "./campaign-steps/templates-sms/templates-sms";
import TemplatesCall from "./campaign-steps/templates-call/templates-call";
import AttackProfile from "./campaign-steps/attacker-profile/attacker-profile";
import AttachmentTemplate from "./campaign-steps/templates/attachment-template";
import TemplatesWhatsApp from "./campaign-steps/templates-message/templates-message";
import TemplatesTelegram from "./campaign-steps/templates-telegram/templates-telegram";
import AttackProfileSMS from "./campaign-steps/attacker-profile-sms/attacker-profile-sms";
import AttackProfileCall from "./campaign-steps/attacker-profile-call/attacker-profile-call";
import AttackProfileWhatsApp from "./campaign-steps/attacker-profile-whatsapp/attacker-profile-whatsapp";
import AttackProfileTelegram from "./campaign-steps/attacker-profile-telegram/attacker-profile-telegram";
import AiConversation from "./campaign-steps/ai-conversation/ai-conversation";
import VoiceSelection from "./campaign-steps/voice-selection/voice-selection";
import RansomwareFile from "./campaign-steps/ransomware-file/ransomware-file";
import Preview from "./campaign-steps/preview/preview";

import "../../container/campaign-template/campaign-create/campaign.css";
import "./campaign-draft-preview.css";
import "./ai-campaign-template.css";

// Step metadata mirrored from nav-tabs.js. `campaign_name` is rendered as the
// first step (a deep copy of the campaign-create CampaignName page) so the user
// can review/edit name, category, language and tags; simulation type / attack
// vector stay locked since they were already chosen in the AI chat.
const STEP_META = {
  campaign_name: { eventKey: "first", title: "Template Name", completed: (p) => !!p.campaignTemplateId },
  attacker_profile: { eventKey: "second", title: "Attacker Profile", completed: (p) => !!p.senderId },
  attacker_profile_sms: { eventKey: "second-sms", title: "Attacker Profile", completed: (p) => !!p.senderId },
  attacker_profile_telegram: { eventKey: "second-telegram", title: "Attacker Profile", completed: (p) => !!p.senderId },
  attacker_profile_call: { eventKey: "second-call", title: "Attacker Profile", completed: (p) => !!p.senderId },
  template: { eventKey: "third", title: "Email Template", completed: (p) => !!p.templateId },
  ai_conversation: { eventKey: "third_ai_conversation", title: "AI Conversation", completed: (p) => !!(p.aiVishingPromptId || p.aiWhatsappPromptId) },
  voice_selection: { eventKey: "voice_selection", title: "Voice Selection", completed: (p) => !!p.vishingVoiceSelectionId },
  template_sms: { eventKey: "third-sms", title: "SMS Template", completed: (p) => !!p.templateId },
  template_telegram: { eventKey: "third-telegram", title: "Message Template", completed: (p) => !!p.templateId },
  page: { eventKey: "fourth", title: "Landing Page", completed: (p) => !!p.pageId },
  ransomware_file: { eventKey: "fifth", title: "Ransomware File", completed: (p) => !!p.ransomwareFileCompleted },
  training: { eventKey: "six", title: "Training", completed: (p) => !!p.trainingId },
  game: { eventKey: "seven", title: "Game", completed: (p) => !!p.gameId },
  awareness_page: { eventKey: "eight", title: "Awareness Page", completed: (p) => !!p.awarenessPageId },
  preview: { eventKey: "nine", title: "Preview", completed: () => false },
};

const GENERIC_TABS = ["campaign_name", "template", "awareness_page", "attacker_profile", "preview"];

// "Edit with prompt" bar shown under the Email / Landing / Awareness steps.
// On submit it calls `onSubmit(prompt)` (provided by the parent), which hits the
// PRA backend → Threatcop-MCP HTML generator and replaces the editor HTML.
class EditWithPromptBar extends React.Component {
  state = { value: "", loading: false };

  handleSubmit = async () => {
    const prompt = this.state.value.trim();
    if (!prompt || this.state.loading) return;
    this.setState({ loading: true });
    try {
      await this.props.onSubmit(prompt);
      this.setState({ value: "", loading: false });
    } catch (e) {
      this.setState({ loading: false });
    }
  };

  handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      this.handleSubmit();
    }
  };

  render() {
    const { label } = this.props;
    const { value, loading } = this.state;
    return (
      <div className={`act-prompt-bar ${loading ? "is-loading" : ""}`}>
        <span className="act-prompt-clip" aria-hidden>📎</span>
        <input
          className="act-prompt-input"
          placeholder={loading ? `Generating ${label}…` : `Edit ${label} with prompt`}
          aria-label={`Edit ${label} with prompt`}
          value={value}
          disabled={loading}
          onChange={(e) => this.setState({ value: e.target.value })}
          onKeyDown={this.handleKeyDown}
        />
        <button
          type="button"
          className="act-prompt-send"
          title="Send"
          disabled={loading || !value.trim()}
          onClick={this.handleSubmit}
        >
          {loading ? (
            <span className="act-prompt-spinner" aria-hidden />
          ) : (
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="22" y1="2" x2="11" y2="13" />
              <polygon points="22 2 15 22 11 13 2 9 22 2" />
            </svg>
          )}
        </button>
      </div>
    );
  }
}

export default class AiCampaignTemplate extends Component {
  constructor(props) {
    super(props);

    // The AI panel is opened to edit an already-selected/created campaign
    // template. `templateId` / `selectedTemplateIds` hold campaign_template ids.
    const ids = getTemplateIds({
      campaignTemplateIds: props.selectedTemplateIds,
      campaignTemplateId: props.templateId,
    });
    const campaignTemplateId = ids[0] || 0;

    this.state = {
      campaignTemplateId,
      loading: true,
      activeTab: null,

      // Context — seeded from props so the steps render immediately, then
      // overwritten by the saved template once `load()` resolves.
      attack_type: [],
      simulation_type: [],
      attack_type_name: normalizeAttackType(props.attackType),
      simulation_type_name: normalizeSimulationType(props.simulationType),
      attackTypeId: -1,
      simulationTypeId: -1,
      credential_harvesting: coerceBoolean(props.credentialHarvesting, false),
      targetInteractionType: normalizeInteractionType(props.targetInteractionType) || "inbound",
      isAIbased: coerceBoolean(props.isAIbased, false),

      // Template Name step fields (hydrated from the saved template).
      templateName: "",
      tags: [],
      campaignNameLoading: false,

      // Per-step record ids (hydrated from the saved template).
      pageId: 0,
      gameId: 0,
      senderId: 0,
      templateId: 0,
      trainingId: 0,
      categoryId: 0,
      languageId: 0,
      awarenessPageId: "",
      aiVishingPromptId: "",
      aiWhatsappPromptId: "",
      vishingVoiceSelectionId: "",
      senderType: "automatic",
      whatsappGateway: "",

      // Step-completion flags resolved asynchronously.
      voiceSelectionCompleted: false,
      voiceWizardVoiceId: "",
      ransomwareFileCompleted: false,

      // AI-generation props consumed by <Templates>; unused here, kept as
      // inert defaults so the reused component behaves like a manual edit.
      subContent: "",
      templateResponse: [],
      currentIndex: 0,
      defaultColor: null,
      backgroundHeader: null,
      logoPosition: null,
    };

    this._lastVoiceStepHydrateSig = null;

    // Refs to the reused step components, so "Edit with prompt" can imperatively
    // push freshly generated HTML into their editors.
    this.emailStepRef = React.createRef();
    this.landingStepRef = React.createRef();
    this.awarenessStepRef = React.createRef();
  }

  componentDidMount() {
    this.load();
  }

  componentDidUpdate(prevProps) {
    // Re-hydrate if the parent re-opens the panel for a different template.
    const ids = getTemplateIds({
      campaignTemplateIds: this.props.selectedTemplateIds,
      campaignTemplateId: this.props.templateId,
    });
    const prevIds = getTemplateIds({
      campaignTemplateIds: prevProps.selectedTemplateIds,
      campaignTemplateId: prevProps.templateId,
    });
    if ((ids[0] || 0) !== (prevIds[0] || 0)) {
      // eslint-disable-next-line react/no-did-update-set-state
      this.setState({ campaignTemplateId: ids[0] || 0, loading: true, activeTab: null }, this.load);
      return;
    }

    const sig = this.getVoiceStepHydrateSignature();
    if (sig == null) {
      if (this._lastVoiceStepHydrateSig != null) this._lastVoiceStepHydrateSig = null;
      return;
    }
    if (sig === this._lastVoiceStepHydrateSig) return;
    this._lastVoiceStepHydrateSig = sig;
    this.hydrateVoiceSelectionStepCompletion(sig);
  }

  load = async () => {
    if (!this.state.campaignTemplateId) {
      // No saved template id — render from prop context, resolve names only.
      this.loadSimulationType();
      return;
    }
    this.setState({ loading: true });
    axios
      .get("/api/campaign/template/" + this.state.campaignTemplateId, {})
      .then((res) => {
        const t = res.data.campaign_template;
        this.setState(
          {
            loading: false,
            templateName: t.name || "",
            tags: (t.campaign_template_tags || []).map((tag) => ({ label: tag.tagName, value: tag.tagName, __isNew__: true })),
            pageId: t.pageId,
            gameId: t.gameId,
            senderId: t.senderId,
            aiVishingPromptId: t.aiVishingPromptId,
            aiWhatsappPromptId: t.aiWhatsappPromptId,
            vishingVoiceSelectionId: t.vishingVoiceSelectionId || "",
            trainingId: t.courseId,
            templateId: t.templateId,
            senderType: t.senderType,
            categoryId: t.categoryId,
            languageId: t.languageId,
            attackTypeId: t.attackTypeId,
            simulationTypeId: t.simulationTypeId,
            credential_harvesting: t.credential_harvesting,
            targetInteractionType: t.targetInteractionType,
            isAIbased: t.isAIbased,
            // The awareness page is keyed by campaignTemplateId — the campaign
            // template itself has no awarenessPageId — so it is hydrated
            // separately below rather than read off `t`.
            awarenessPageId: "",
            voiceSelectionCompleted: false,
            voiceWizardVoiceId: "",
          },
          () => {
            this._lastVoiceStepHydrateSig = null;
            this.loadSimulationType();
            this.hydrateAwarenessPageCompletion(this.state.campaignTemplateId);
          }
        );
      })
      .catch((error) => {
        this.setState({ loading: false });
        apiError(error);
      });
  };

  loadSimulationType = async () => {
    this.setState({ loading: true });
    axios
      .get("/api/simulation/type", {})
      .then((res) => {
        const simList = res.data.simulation_type || [];
        this.setState({ loading: false, simulation_type: simList });
        const wantSimId = Number(this.state.simulationTypeId);
        for (let i = 0; i < simList.length; i++) {
          if (Number(simList[i].id) === wantSimId) {
            this.setState({ simulation_type_name: simList[i].name });
            break;
          }
        }
      })
      .catch((error) => {
        this.setState({ loading: false });
        apiError(error);
      });

    this.loadAttackVectors();
  };
  
  loadAttackVectors = async () => {
    axios
      .get("/api/attacktypes", {})
      .then((res) => {
        const list = res.data.attackTypes || [];
        const attacks = list.map((a) => ({ attack_type: { id: a.id, name: a.name } }));
        const wantAttackId = Number(this.state.attackTypeId);
        let attack_type_name = this.state.attack_type_name;
        for (let i = 0; i < attacks.length; i++) {
          const at = attacks[i].attack_type;
          if (Number(at.id) === wantAttackId) {
            attack_type_name = at.name;
            break;
          }
        }
        this.setState({ attack_type: attacks, attack_type_name }, () => {
          this._lastVoiceStepHydrateSig = null;
          const sig = this.getVoiceStepHydrateSignature();
          if (sig != null) {
            this._lastVoiceStepHydrateSig = sig;
            this.hydrateVoiceSelectionStepCompletion(sig);
          }
          if (attack_type_name === "Ransomware" && this.state.pageId) {
            this.hydrateRansomwareFileCompletion(this.state.pageId);
          }
        });
      })
      .catch((error) => apiError(error));
  };

  isVoiceDeepfakeEnabled = () => isTruthyValue(getCookie("isDeepFakeEnabled"));

  getVoiceStepHydrateSignature = () => {
    const { attack_type_name, targetInteractionType, campaignTemplateId, vishingVoiceSelectionId } = this.state;
    if (attack_type_name !== "Vishing" || targetInteractionType !== "inbound") return null;
    if (!campaignTemplateId) return null;
    if (vishingVoiceSelectionId == null || String(vishingVoiceSelectionId).trim() === "") return null;
    return `${campaignTemplateId}|v|${String(vishingVoiceSelectionId)}`;
  };

  hydrateVoiceSelectionStepCompletion = (sig) => {
    const { vishingVoiceSelectionId } = this.state;
    if (!vishingVoiceSelectionId) return;
    axios
      .get(`/api/vishing-voice-selection/${vishingVoiceSelectionId}`)
      .then((res) => {
        if (this.getVoiceStepHydrateSignature() !== sig) return;
        const voiceSel = res.data?.vishingVoiceSelection || res.data?.data?.vishingVoiceSelection || {};
        const rawVid = voiceSel.voiceId;
        const hasVid = rawVid != null && String(rawVid).trim() !== "";
        const nextWizardVid = hasVid ? String(rawVid) : "";
        this.setState((prev) => {
          if (prev.voiceSelectionCompleted === hasVid && prev.voiceWizardVoiceId === nextWizardVid) return null;
          return { voiceSelectionCompleted: hasVid, voiceWizardVoiceId: nextWizardVid };
        });
      })
      .catch(() => {
        if (this.getVoiceStepHydrateSignature() === sig) this._lastVoiceStepHydrateSig = null;
      });
  };

  // The awareness page record stores campaignTemplateId (not the reverse), so
  // resolve the linked page's id from the awareness-page endpoint to drive the
  // step-completion flag — same source AwarenessPages reads from.
  hydrateAwarenessPageCompletion = (campaignTemplateId) => {
    if (!campaignTemplateId || this.state.attack_type_name === "Ransomware") return;
    axios
      .get("/api/campaign/template/awareness-page", { params: { campaignTemplateId } })
      .then((res) => {
        const id = res.data?.id;
        if (id) this.setState({ awarenessPageId: id });
      })
      .catch(() => {});
  };

  hydrateRansomwareFileCompletion = (pageId) => {
    if (this.state.attack_type_name !== "Ransomware" || !pageId || parseInt(pageId, 10) === 0) return;
    axios
      .get("/api/pages/" + pageId, {})
      .then((res) => {
        const page = res.data.page || {};
        const fileName = page.ransonware_file || "";
        const fileId = page.ransomwareFileId || 0;
        const completed = fileName && String(fileName).trim().length > 0 && parseInt(fileId, 10) > 0;
        if (completed) this.setState({ ransomwareFileCompleted: true });
      })
      .catch(() => {});
  };

  // ── Template Name step handlers (mirrors campaign-create) ──
  // Simulation type / attack vector are locked here (disableState), so their
  // dropdowns never fire onChange — the no-op handlers exist only to satisfy the
  // reused CampaignName component.
  handleCampaignName = (e) => this.setState({ templateName: e.target.value });
  handleCategoryId = (e) => this.setState({ categoryId: e.target.value });
  handleLanguageId = (e) => this.setState({ languageId: e.target.value });
  setTags = (tagValues) => this.setState({ tags: tagValues });
  handleHarvesting = () => this.setState((prev) => ({ credential_harvesting: !prev.credential_harvesting }));
  handleSimulationType = () => {};
  handleAttackType = () => {};
  handleTargetInteractionType = (type) => this.setState({ targetInteractionType: type });
  handleAIbased = (isAIbased) =>
    this.setState({ isAIbased, targetInteractionType: isAIbased ? "inbound" : "outbound" });

  handleValidate = () => {
    if (!this.state.templateName || this.state.templateName.trim().length === 0) {
      toast.error("Please Enter Template Name");
      return false;
    } else if (parseInt(this.state.categoryId, 10) === -1 || parseInt(this.state.categoryId, 10) === 0) {
      toast.error("Please Select Category");
      return false;
    } else if (parseInt(this.state.languageId, 10) === 0) {
      toast.error("Please Select Language");
      return false;
    } else if (parseInt(this.state.attackTypeId, 10) === -1) {
      toast.error("Please Select Attack Vector");
      return false;
    } else if (
      (this.state.attack_type_name === "Vishing" || this.state.attack_type_name === "WhatsApp") &&
      this.state.targetInteractionType !== "inbound" &&
      this.state.targetInteractionType !== "outbound"
    ) {
      toast.error(`Please Select ${this.state.attack_type_name} Mode`);
      return false;
    }
    return {
      categoryId: this.state.categoryId,
      languageId: this.state.languageId,
      attackTypeId: this.state.attackTypeId,
      name: this.state.templateName.trim(),
      tags: this.state.tags ? this.state.tags.map((tag) => tag.value) : [],
      targetInteractionType: this.state.targetInteractionType,
      isAIbased: this.state.isAIbased,
    };
  };

  // The campaign template already exists (created by the AI), so this always
  // PATCHes the saved record with the reviewed name / category / language / tags,
  // then advances to the next step.
  handleCreateCampaignTemplate = () => {
    const data = this.handleValidate();
    if (!data || !this.state.campaignTemplateId) return;
    this.setState({ campaignNameLoading: true, loading: true });
    axios
      .patch("/api/campaign/template/" + this.state.campaignTemplateId, data)
      .then(() => {
        this.setState({ campaignNameLoading: false, loading: false });
        this.handleTabChange("next");
      })
      .catch((error) => {
        this.setState({ campaignNameLoading: false, loading: false });
        apiError(error);
      });
  };

  // ── Step component handlers (mirrors campaign-create) ──
  handleSenderId = (senderId) => this.setState({ senderId });
  handleTemplateId = (templateId) => this.setState({ templateId });
  handleTraining = (trainingId) => this.setState({ trainingId });
  handleAwarenessPageId = (awarenessPageId) => this.setState({ awarenessPageId });
  handleWhatsappGateway = (whatsappGateway) => this.setState({ whatsappGateway });
  handleRansomwareFileComplete = () => this.setState({ ransomwareFileCompleted: true });
  handleVoiceSelectionStepComplete = () => this.setState({ voiceSelectionCompleted: true });
  updateBackgroundHeader = (backgroundHeader) => this.setState({ backgroundHeader });
  updateLogoPosition = (logoPosition) => this.setState({ logoPosition });

  handlePageId = (pageId) => {
    this.setState({ pageId }, () => this.hydrateRansomwareFileCompletion(pageId));
  };

  handleSenderType = () => {
    axios
      .post("/api/campaign/template/senderType/" + this.state.campaignTemplateId, {})
      .then((res) => this.setState({ senderType: res.data.senderType }))
      .catch((error) => apiError(error));
  };

  handleAiVishingPromptId = (aiVishingPromptId) => this.setState({ aiVishingPromptId });
  handleAiWhatsappPromptId = (aiWhatsappPromptId) => this.setState({ aiWhatsappPromptId });

  handleVishingVoiceSelectionCreated = (vishingVoiceSelectionId) => {
    this.setState((prev) => {
      if (String(prev.vishingVoiceSelectionId ?? "") !== String(vishingVoiceSelectionId ?? "")) {
        this._lastVoiceStepHydrateSig = null;
        return { vishingVoiceSelectionId, voiceSelectionCompleted: false, voiceWizardVoiceId: "" };
      }
      return null;
    });
  };

  // Navigation used by the Preview step (Finish / Create Campaign / Test).
  // Prefer a router-aware nextPath from the parent; otherwise fall back to
  // closing the panel so we never crash inside the AI side-panel.
  nextPath = (path) => {
    if (typeof this.props.nextPath === "function") this.props.nextPath(path);
    else if (typeof this.props.onClose === "function") this.props.onClose();
  };

  // ── "Edit with prompt" handlers ──
  // Each posts the user's prompt + campaign context to the PRA backend, which
  // calls the Threatcop-MCP HTML generator and returns HTML. The returned HTML
  // replaces whatever is currently in the matching step's editor.
  handleEmailPromptEdit = async (prompt) => {
    try {
      const res = await axios.post("/api/campaign/template/edit-with-prompt/email", {
        prompt,
        attackType: this.state.attack_type_name,
        html: this.emailStepRef.current ? this.emailStepRef.current.getHtml() : "",
      });
      const html = res.data && res.data.html;
      if (html && this.emailStepRef.current) {
        this.emailStepRef.current.applyGeneratedHtml(html);
        toast.success("Email template updated");
      }
    } catch (error) {
      apiError(error);
      throw error;
    }
  };

  handleLandingPromptEdit = async (prompt) => {
    try {
      const res = await axios.post("/api/campaign/template/edit-with-prompt/landing-page", {
        prompt,
        captureCredentials: coerceBoolean(this.state.credential_harvesting, false),
        html: this.landingStepRef.current ? this.landingStepRef.current.getHtml() : "",
      });
      const html = res.data && res.data.html;
      if (html && this.landingStepRef.current) {
        this.landingStepRef.current.applyGeneratedHtml(html);
        toast.success("Landing page updated");
      }
    } catch (error) {
      apiError(error);
      throw error;
    }
  };

  handleAwarenessPromptEdit = async (prompt) => {
    try {
      const res = await axios.post("/api/campaign/template/edit-with-prompt/awareness-page", {
        prompt,
        attackType: this.state.attack_type_name,
        simulationType: this.state.simulation_type_name,
        html: this.awarenessStepRef.current ? this.awarenessStepRef.current.getHtml() : "",
      });
      const html = res.data && res.data.html;
      if (html && this.awarenessStepRef.current) {
        this.awarenessStepRef.current.applyGeneratedHtml(html);
        toast.success("Awareness page updated");
      }
    } catch (error) {
      apiError(error);
      throw error;
    }
  };

  // Ordered list of visible steps, derived from campaignTabs using the same
  // gating rules as nav-tabs.js, with `campaign_name` removed.
  getVisibleSteps = () => {
    const { simulation_type_name, attack_type_name, targetInteractionType, isAIbased, credential_harvesting } = this.state;

    let tabNames;
    if (
      targetInteractionType === "inbound" &&
      (attack_type_name === "Vishing" || (attack_type_name === "WhatsApp" && isAIbased))
    ) {
      tabNames = campaignTabs?.[simulation_type_name]?.[attack_type_name]?.inbound || GENERIC_TABS;
    } else {
      tabNames = campaignTabs?.[simulation_type_name]?.[attack_type_name] || GENERIC_TABS;
    }

    const steps = [];
    (tabNames || []).forEach((tabName) => {
      // Landing Page is shown per attack vector; credential harvesting is a campaign-level setting now.
      const meta = STEP_META[tabName];
      if (!meta) return;
      steps.push({
        eventKey: meta.eventKey,
        title: meta.title,
        completed: meta.completed(this.state),
      });
    });

    // Disable steps until prior steps are complete; the active step stays enabled.
    const activeKey = this.getActiveKey(steps);
    steps.forEach((step, idx) => {
      let priorIncomplete = false;
      for (let j = 0; j < idx; j++) {
        if (!steps[j].completed) {
          priorIncomplete = true;
          break;
        }
      }
      step.isActive = step.eventKey === activeKey;
      step.navDisabled = priorIncomplete && !step.isActive;
    });

    return steps;
  };

  getActiveKey = (steps) => {
    const list = steps || this.getVisibleSteps();
    const keys = list.map((s) => s.eventKey);
    if (this.state.activeTab && keys.includes(this.state.activeTab)) return this.state.activeTab;
    return keys[0] || null;
  };

  handleTabChange = (type) => {
    const steps = this.getVisibleSteps();
    const keys = steps.map((s) => s.eventKey);
    const activeKey = this.getActiveKey(steps);
    const idx = keys.indexOf(activeKey);
    if (idx === -1) return;
    if (type === "next" && idx < keys.length - 1) {
      this.setState({ activeTab: keys[idx + 1] });
    } else if (type === "prev" && idx > 0) {
      this.setState({ activeTab: keys[idx - 1] });
    }
  };

  render() {
    const { attack_type_name, targetInteractionType, isAIbased, campaignTemplateId } = this.state;
    const isAiWhatsapp = attack_type_name === "WhatsApp" && targetInteractionType === "inbound" && isAIbased;
    const isNormalWhatsapp = attack_type_name === "WhatsApp" && !isAiWhatsapp;

    const visibleSteps = this.getVisibleSteps();
    const activeKey = this.getActiveKey(visibleSteps);

    return (
      <div className="ec-wrapper act-wrapper template-wizard act-horizontal-wizard">
        <Tab.Container
          id="ai-campaign-template-tabs"
          activeKey={activeKey || undefined}
          onSelect={(activeTab) => this.setState({ activeTab })}
        >
          {/* ── Horizontal step bar (top) ── */}
          <div className="act-topbar">
            <div className="act-tabs-scroll">
              <Nav variant="pills" className="act-tabs-nav">
                {visibleSteps.map((step, idx) => {
                  const itemClass = [
                    "act-tab-item",
                    step.isActive ? "active" : "",
                    step.completed ? "completed" : "",
                    step.navDisabled ? "disabled" : "",
                  ]
                    .filter(Boolean)
                    .join(" ");
                  return (
                    <Nav.Item key={step.eventKey} className={itemClass}>
                      <Nav.Link
                        eventKey={step.eventKey}
                        disabled={step.navDisabled}
                        className={`act-tab-link ${step.isActive ? "active" : ""}`}
                      >
                        <span className="act-tab-num">
                          {step.completed ? <PiCheckCircleFill className="act-tab-check" /> : idx + 1}
                        </span>
                        <span className="act-tab-title">
                          {step.title}
                          {step.eventKey === "voice_selection" && this.isVoiceDeepfakeEnabled() ? (
                            <span className="act-deepfake-badge" role="note">
                              <PiSparkleBold aria-hidden /> Deepfake
                            </span>
                          ) : null}
                        </span>
                      </Nav.Link>
                    </Nav.Item>
                  );
                })}
              </Nav>
            </div>

            <button type="button" className="ec-close-btn act-close-btn" onClick={this.props.onClose} aria-label="Close">
              ×
            </button>
          </div>

          {/* ── Step content ── */}
          <Tab.Content className="act-content">
            {/* Template Name — deep copy of the campaign-create CampaignName page.
                Name / category / language / tags are editable; simulation type and
                attack vector are locked (already chosen in the AI chat). */}
            <Tab.Pane eventKey="first">
              <CampaignName
                templateName={this.state.templateName}
                categoryId={this.state.categoryId}
                languageId={this.state.languageId}
                tags={this.state.tags}
                simulation_type={this.state.simulation_type}
                attack_type={this.state.attack_type}
                simulationTypeId={this.state.simulationTypeId}
                attackTypeId={this.state.attackTypeId}
                simulationTypeName={this.state.simulation_type_name}
                attackTypeName={attack_type_name}
                credential_harvesting={this.state.credential_harvesting}
                targetInteractionType={targetInteractionType}
                isAIbased={isAIbased}
                campaignTemplateId={campaignTemplateId}
                disableState={true}
                showNextTab={true}
                campaignNameLoading={this.state.campaignNameLoading}
                handleCampaignName={this.handleCampaignName}
                handleCategoryId={this.handleCategoryId}
                handleLanguageId={this.handleLanguageId}
                setTags={this.setTags}
                handleHarvesting={this.handleHarvesting}
                handleSimulationType={this.handleSimulationType}
                handleAttackType={this.handleAttackType}
                handleTargetInteractionType={this.handleTargetInteractionType}
                handleAIbased={this.handleAIbased}
                handleCreateCampaignTemplate={this.handleCreateCampaignTemplate}
              />
            </Tab.Pane>

            {/* Attacker Profile (email/QR/ransomware/attachment/vishing-inbound/AI-WhatsApp/Training+Quiz) */}
            {attack_type_name === "Phishing" ||
            attack_type_name === "QRcode" ||
            attack_type_name === "Ransomware" ||
            attack_type_name === "Attachment" ||
            (attack_type_name === "Vishing" && targetInteractionType === "inbound") ||
            isAiWhatsapp ||
            attack_type_name === "Training + Quiz" ? (
              <Tab.Pane eventKey="second">
                <AttackProfile
                  senderId={this.state.senderId}
                  senderType={this.state.senderType}
                  handleSenderId={this.handleSenderId}
                  handleTabChange={this.handleTabChange}
                  handleSenderType={this.handleSenderType}
                  campaignTemplateId={campaignTemplateId}
                  attackType={attack_type_name}
                  targetInteractionType={targetInteractionType}
                  isAIbased={isAIbased}
                />
              </Tab.Pane>
            ) : null}

            {(attack_type_name === "Vishing" && targetInteractionType === "inbound") || isAiWhatsapp ? (
              <Tab.Pane eventKey="third_ai_conversation">
                <AiConversation
                  aiVishingPromptId={this.state.aiVishingPromptId}
                  aiWhatsappPromptId={this.state.aiWhatsappPromptId}
                  vishingVoiceSelectionId={this.state.vishingVoiceSelectionId}
                  handleAiVishingPromptId={this.handleAiVishingPromptId}
                  handleAiWhatsappPromptId={this.handleAiWhatsappPromptId}
                  handleTabChange={this.handleTabChange}
                  campaignTemplateId={campaignTemplateId}
                  attackType={attack_type_name}
                  promptType={isAiWhatsapp ? "whatsapp" : "vishing"}
                />
              </Tab.Pane>
            ) : null}

            {attack_type_name === "Vishing" && targetInteractionType === "inbound" ? (
              <Tab.Pane eventKey="voice_selection">
                <VoiceSelection
                  handleTabChange={this.handleTabChange}
                  vishingVoiceSelectionId={this.state.vishingVoiceSelectionId}
                  campaignTemplateId={campaignTemplateId}
                  onVishingVoiceSelectionCreated={this.handleVishingVoiceSelectionCreated}
                  onVoiceSelectionStepComplete={this.handleVoiceSelectionStepComplete}
                />
              </Tab.Pane>
            ) : null}

            {attack_type_name === "Telegram" ? (
              <Tab.Pane eventKey="second-telegram">
                <AttackProfileTelegram
                  senderId={this.state.senderId}
                  handleSenderId={this.handleSenderId}
                  handleTabChange={this.handleTabChange}
                  campaignTemplateId={campaignTemplateId}
                />
              </Tab.Pane>
            ) : null}

            {attack_type_name === "Smishing" ? (
              <Tab.Pane eventKey="second-sms">
                <AttackProfileSMS
                  senderId={this.state.senderId}
                  handleSenderId={this.handleSenderId}
                  handleTabChange={this.handleTabChange}
                  campaignTemplateId={campaignTemplateId}
                />
              </Tab.Pane>
            ) : null}

            {isNormalWhatsapp ? (
              <Tab.Pane eventKey="second-sms">
                <AttackProfileWhatsApp
                  senderId={this.state.senderId}
                  handleSenderId={this.handleSenderId}
                  handleTabChange={this.handleTabChange}
                  campaignTemplateId={campaignTemplateId}
                  handleWhatsappGateway={this.handleWhatsappGateway}
                />
              </Tab.Pane>
            ) : null}

            {attack_type_name === "Vishing" && targetInteractionType === "outbound" ? (
              <Tab.Pane eventKey="second-call">
                <AttackProfileCall
                  senderId={this.state.senderId}
                  handleSenderId={this.handleSenderId}
                  handleTabChange={this.handleTabChange}
                  campaignTemplateId={campaignTemplateId}
                />
              </Tab.Pane>
            ) : null}

            {/* Email Template — carries the Edit-with-prompt bar */}
            {attack_type_name === "Phishing" ||
            attack_type_name === "Ransomware" ||
            attack_type_name === "QRcode" ||
            (attack_type_name === "Vishing" && targetInteractionType === "inbound") ||
            isAiWhatsapp ? (
              <Tab.Pane eventKey="third">
                <Templates
                  ref={this.emailStepRef}
                  templateId={this.state.templateId}
                  subContent={this.state.subContent}
                  handleTabChange={this.handleTabChange}
                  handleTemplateId={this.handleTemplateId}
                  campaignTemplateId={campaignTemplateId}
                  templateResponse={this.state.templateResponse}
                  currentIndex={this.state.currentIndex}
                  defaultColor={this.state.defaultColor}
                  updateBackgroundHeader={this.updateBackgroundHeader}
                  updateLogoPosition={this.updateLogoPosition}
                  backgroundHeader={this.state.backgroundHeader}
                  logoPosition={this.state.logoPosition}
                  attackType={attack_type_name}
                  targetInteractionType={targetInteractionType}
                  isAIbased={isAIbased}
                  promptBar={<EditWithPromptBar label="email template" onSubmit={this.handleEmailPromptEdit} />}
                />
              </Tab.Pane>
            ) : null}

            {attack_type_name === "Attachment" ? (
              <Tab.Pane eventKey="third">
                <AttachmentTemplate
                  templateId={this.state.templateId}
                  subContent={this.state.subContent}
                  handleTabChange={this.handleTabChange}
                  handleTemplateId={this.handleTemplateId}
                  campaignTemplateId={campaignTemplateId}
                  templateResponse={this.state.templateResponse}
                  currentIndex={this.state.currentIndex}
                  defaultColor={this.state.defaultColor}
                  updateBackgroundHeader={this.updateBackgroundHeader}
                  updateLogoPosition={this.updateLogoPosition}
                  backgroundHeader={this.state.backgroundHeader}
                  logoPosition={this.state.logoPosition}
                />
              </Tab.Pane>
            ) : null}

            {attack_type_name === "Telegram" ? (
              <Tab.Pane eventKey="third-telegram">
                <TemplatesTelegram
                  senderId={this.state.senderId}
                  templateId={this.state.templateId}
                  handleTabChange={this.handleTabChange}
                  handleTemplateId={this.handleTemplateId}
                  campaignTemplateId={campaignTemplateId}
                />
              </Tab.Pane>
            ) : null}

            {attack_type_name === "Smishing" ? (
              <Tab.Pane eventKey="third-sms">
                <TemplatesSMS
                  templateId={this.state.templateId}
                  handleTabChange={this.handleTabChange}
                  handleTemplateId={this.handleTemplateId}
                  campaignTemplateId={campaignTemplateId}
                />
              </Tab.Pane>
            ) : null}

            {isNormalWhatsapp ? (
              <Tab.Pane eventKey="third-sms">
                <TemplatesWhatsApp
                  senderId={this.state.senderId}
                  templateId={this.state.templateId}
                  handleTabChange={this.handleTabChange}
                  handleTemplateId={this.handleTemplateId}
                  campaignTemplateId={campaignTemplateId}
                  whatsappGateway={this.state.whatsappGateway}
                />
              </Tab.Pane>
            ) : null}

            {attack_type_name === "Vishing" && targetInteractionType === "outbound" ? (
              <Tab.Pane eventKey="third-call">
                <TemplatesCall
                  templateId={this.state.templateId}
                  handleTabChange={this.handleTabChange}
                  handleTemplateId={this.handleTemplateId}
                  campaignTemplateId={campaignTemplateId}
                />
              </Tab.Pane>
            ) : null}

            {/* Landing Page — carries the Edit-with-prompt bar */}
            <Tab.Pane eventKey="fourth">
              <Pages
                ref={this.landingStepRef}
                pageId={this.state.pageId}
                handlePageId={this.handlePageId}
                handleTabChange={this.handleTabChange}
                attack_type_name={attack_type_name}
                campaignTemplateId={campaignTemplateId}
                promptBar={<EditWithPromptBar label="landing page" onSubmit={this.handleLandingPromptEdit} />}
              />
            </Tab.Pane>

            {attack_type_name === "Ransomware" ? (
              <Tab.Pane eventKey="fifth" mountOnEnter>
                <RansomwareFile
                  pageId={this.state.pageId}
                  campaignTemplateId={campaignTemplateId}
                  isActive={activeKey === "fifth"}
                  handleTabChange={this.handleTabChange}
                  onRansomwareFileComplete={this.handleRansomwareFileComplete}
                />
              </Tab.Pane>
            ) : null}

            <Tab.Pane eventKey="six">
              {this.state.simulation_type_name.indexOf("Training") > -1 && (
                <Training
                  trainingId={this.state.trainingId}
                  handleTraining={this.handleTraining}
                  handleTabChange={this.handleTabChange}
                  campaignTemplateId={campaignTemplateId}
                />
              )}
            </Tab.Pane>

            <Tab.Pane eventKey="seven">
              <Game gameId={this.state.gameId} handleTabChange={this.handleTabChange} campaignTemplateId={campaignTemplateId} />
            </Tab.Pane>

            {/* Awareness Page — carries the Edit-with-prompt bar */}
            {attack_type_name !== "Ransomware" && (
              <Tab.Pane eventKey="eight">
                <AwarenessPages
                  ref={this.awarenessStepRef}
                  pageId={this.state.pageId}
                  handlePageId={this.handlePageId}
                  handleTabChange={this.handleTabChange}
                  attack_type_name={attack_type_name}
                  campaignTemplateId={campaignTemplateId}
                  handleAwarenessPageId={this.handleAwarenessPageId}
                  promptBar={<EditWithPromptBar label="awareness page" onSubmit={this.handleAwarenessPromptEdit} />}
                />
              </Tab.Pane>
            )}

            {/* Preview — overall view of the campaign template before saving */}
            <Tab.Pane eventKey="nine" mountOnEnter>
              <Preview
                campaignTemplateId={campaignTemplateId}
                nextPath={this.nextPath}
                onFinish={this.props.onFinish}
                onClose={this.props.onClose}
              />
            </Tab.Pane>
          </Tab.Content>
        </Tab.Container>

        {this.state.loading ? <Loader /> : null}
      </div>
    );
  }
}
